// PlayFirst Parent — Welcome / entry screen. Reachable at /parent-app
// (publicly). Inspired by the existing Lite hub feel but in a warmer
// pastel palette. Two CTAs: Create my child's Language Map (signup)
// and Sign in (if returning).
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, ArrowRight, MessageCircle, ShieldCheck, Package } from 'lucide-react';
import { PFP } from './styles';

export default function ParentAppWelcome() {
  return (
    <div className={PFP.page}>
      {/* Subtle top bar — just brand + sign-in link */}
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-2">
          <Heart className="w-6 h-6 text-[#7C6F9C] fill-[#7C6F9C]/20" />
          <span className="font-serif text-xl text-[#3a2f4f]">PlayFirst</span>
          <span className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium ml-1">
            Parent
          </span>
        </Link>
        <Link
          to="/parent-app/login"
          className="text-sm text-[#7C6F9C] hover:underline"
          data-testid="parent-app-signin-link"
        >
          Sign in
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-8 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 backdrop-blur border border-[#7C6F9C]/15 text-[#7C6F9C] text-xs uppercase tracking-wider mb-6">
            <Sparkles className="w-3.5 h-3.5" /> A gentle companion for parents
          </div>
          <h1 className={`${PFP.h1} mb-5`}>
            If you're worried, googling at 2am, or just want to play more — you're in the right place.
          </h1>
          <p className={`${PFP.sub} mb-8 max-w-2xl`}>
            PlayFirst is a warm, neurodiversity-affirming companion that helps you understand
            how your little one communicates today — and gives you one small, personalised play
            idea at a time. From newborn onwards.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-10">
            <Link to="/parent-app/signup">
              <button
                className={PFP.btnPrimary + ' inline-flex items-center gap-2'}
                data-testid="parent-app-start-btn"
              >
                Start with my child's Language Map
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/parent-app/login">
              <button className={PFP.btnGhost} data-testid="parent-app-login-btn">
                I already have an account
              </button>
            </Link>
          </div>

          <p className="text-sm text-stone-500 mb-12">
            No diagnosis. No checklists. No pressure. Just play, in your home, at your child's pace.
          </p>

          {/* Value props */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ValueCard
              icon={<Sparkles className="w-5 h-5" />}
              title="One small idea at a time"
              body="A play idea shaped by how your child already communicates — gestures, sounds, scripts, signs, words. All of it counts."
            />
            <ValueCard
              icon={<Package className="w-5 h-5" />}
              title="Ideas for your child's actual toys"
              body="Snap a photo of a favourite toy or interest — we'll craft a tailored play idea around it, anchored to your child's Language Map."
            />
            <ValueCard
              icon={<MessageCircle className="w-5 h-5" />}
              title="A gentle nudge on WhatsApp"
              body="Daily play ideas and warm weekly check-ins on the chat app you already use. Pause anytime."
            />
            <ValueCard
              icon={<ShieldCheck className="w-5 h-5" />}
              title="From birth onwards"
              body="Built for early communication — pre-verbal cues, scripts, signs, AAC, first words and beyond."
            />
          </div>

          {/* Pricing strip */}
          <Link to="/parent-app/pricing" className="block mt-10" data-testid="parent-welcome-pricing-link">
            <div className="p-5 rounded-3xl bg-white/70 backdrop-blur border border-[#7C6F9C]/15 hover:border-[#7C6F9C]/40 hover:shadow-md transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm text-stone-500 mb-0.5">Simple, gentle pricing</p>
                <p className="text-2xl font-serif text-[#3a2f4f]">
                  $6.99 <span className="text-base text-stone-500">/ month</span>
                  <span className="text-base text-stone-400 mx-2">·</span>
                  $59.99 <span className="text-base text-stone-500">/ year</span>
                </p>
              </div>
              <div className="text-sm text-[#7C6F9C] font-medium inline-flex items-center gap-1">
                See what's included <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
        </motion.div>
      </main>

      <footer className="py-8 px-4 text-center text-xs text-stone-400 space-y-2">
        <div className="space-x-3">
          <Link to="/parent-app/contact" className="hover:underline" data-testid="welcome-contact-link">Contact &amp; Support</Link>
          <span aria-hidden>·</span>
          <Link to="/privacy" className="hover:underline">Privacy</Link>
          <span aria-hidden>·</span>
          <Link to="/terms" className="hover:underline">Terms</Link>
        </div>
        <p>
          PlayFirst Parent is not a diagnostic, screening or emergency tool. If you&apos;re
          concerned about your child&apos;s communication or development, please speak with
          a qualified health professional.
        </p>
      </footer>
    </div>
  );
}

function ValueCard({ icon, title, body }) {
  return (
    <div className={`${PFP.card} p-5`}>
      <div className="w-9 h-9 rounded-xl bg-[#7C6F9C]/10 text-[#7C6F9C] flex items-center justify-center mb-3">
        {icon}
      </div>
      <h3 className="font-medium text-[#3a2f4f] mb-1">{title}</h3>
      <p className="text-sm text-stone-600 leading-relaxed">{body}</p>
    </div>
  );
}
